import React, { useState, useEffect, useRef, CSSProperties } from "react";
import ReactDOM from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from "date-fns/locale";
import { DateService } from "../../../utils/dateUtils";
import styles from "./DeliveryDatePicker.module.css";

// Register Spanish locale
registerLocale("es", es);

interface Props {
  groupId: string;
  minDate: Date;
  deliveryDate: string;
  onDateChange: (deliveryDate: string) => void;
  forceShowPlaceholder?: boolean;
  availableDates?: Date[]; // Optional array of available dates for rápido delivery
  deliveryMethod?: string; // Group's current delivery method (standard, rapido, express)
}

const DeliveryDatePicker: React.FC<Props> = ({
  groupId,
  minDate,
  deliveryDate,
  onDateChange,
  forceShowPlaceholder = false,
  availableDates, // Extract available dates prop
  deliveryMethod, // Extract delivery method prop
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarPositionRef = useRef<CSSProperties>({});

  // Add an operation counter to track changes
  const operationCounter = useRef(0);
  const prevDeliveryDate = useRef(deliveryDate);
  const prevMinDate = useRef<Date | null>(null);
  const prevAvailableDates = useRef<Date[] | undefined>(undefined);

  const getNextOperationId = () => {
    operationCounter.current += 1;
    return operationCounter.current;
  };

  // Get portal container reference
  const portalContainer = document.body;

  // Debug log for props tracking
  console.log(`📥 [DELIVERY-DATE-PICKER] Component render with props:`, {
    groupId,
    minDate: minDate.toISOString(),
    minDateLocal: minDate.toLocaleDateString("es-PE"),
    daysFromToday: Math.ceil(
      (minDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    ),
    deliveryDate,
    availableDates: availableDates ? `${availableDates.length} dates` : "none",
    deliveryMethod: deliveryMethod || "not provided",
    forceShowPlaceholder,
  });

  // Log props changes for debugging
  useEffect(() => {
    console.log(
      `📥 [DELIVERY-DATE-PICKER] Props received/updated for group ${groupId}:`,
      {
        groupId,
        minDate: {
          iso: minDate.toISOString(),
          local: minDate.toLocaleDateString("es-PE"),
        },
        deliveryDate,
        forceShowPlaceholder,
        availableDates: availableDates
          ? {
              count: availableDates.length,
              dates: availableDates.map((d) => ({
                iso: d.toISOString(),
                local: d.toLocaleDateString("es-PE"),
              })),
            }
          : "undefined",
        propsChanged: {
          minDateChanged: prevMinDate.current
            ? minDate.getTime() !== prevMinDate.current.getTime()
            : true,
          deliveryDateChanged: prevDeliveryDate.current !== deliveryDate,
          availableDatesChanged:
            JSON.stringify(prevAvailableDates.current) !==
            JSON.stringify(availableDates),
        },
      }
    );

    // Update refs
    prevMinDate.current = minDate;
    prevDeliveryDate.current = deliveryDate;
    prevAvailableDates.current = availableDates;
  }, [
    groupId,
    minDate,
    deliveryDate,
    forceShowPlaceholder,
    availableDates,
    deliveryMethod,
  ]);

  // Handle Express delivery automatically
  useEffect(() => {
    if (deliveryMethod === "express" && !deliveryDate) {
      // Express delivery: automatically set today's date
      const today = new Date();
      const todayNoon = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        12,
        0,
        0
      );

      // Format date using the UI format (DD / MM / YYYY)
      const formattedDate = `${todayNoon
        .getDate()
        .toString()
        .padStart(2, "0")} / ${(todayNoon.getMonth() + 1)
        .toString()
        .padStart(2, "0")} / ${todayNoon.getFullYear()}`;

      console.log(
        `🚀 [DELIVERY-DATE-PICKER] Auto-setting Express delivery date:`,
        {
          groupId,
          deliveryMethod,
          autoSelectedDate: {
            iso: todayNoon.toISOString(),
            local: todayNoon.toLocaleDateString("es-PE"),
            formatted: formattedDate,
          },
          businessRule: "Express delivery uses today's date automatically",
        }
      );

      // Set the selected date and call the callback
      setSelectedDate(todayNoon);
      setShowPlaceholder(false);
      onDateChange(formattedDate);
    }
  }, [deliveryMethod, deliveryDate, groupId, onDateChange]);

  // Check if delivery date was cleared (location change scenario)
  useEffect(() => {
    if (prevDeliveryDate.current && !deliveryDate) {
      // If delivery date was cleared, show the placeholder
      setShowPlaceholder(true);
      setSelectedDate(null);
    } else if (deliveryDate && !forceShowPlaceholder) {
      // If we have a delivery date and not forcing placeholder, don't show placeholder
      setShowPlaceholder(false);
    }

    // Force placeholder if the prop is true, regardless of other conditions
    if (forceShowPlaceholder) {
      setShowPlaceholder(true);
      setSelectedDate(null);
    }

    prevDeliveryDate.current = deliveryDate;
  }, [deliveryDate, forceShowPlaceholder]);

  // Track minDate changes - important for respecting minDate when editing
  useEffect(() => {
    // Check if minDate has changed to a later date
    if (prevMinDate.current && minDate > prevMinDate.current) {
      // If current selected date is before new minDate, update it
      if (selectedDate && selectedDate < minDate) {
        setSelectedDate(minDate);
      }
    }

    prevMinDate.current = minDate;
  }, [minDate, selectedDate]);

  // Parse delivery date using DateService - update display value only, don't open calendar
  useEffect(() => {
    if (deliveryDate) {
      const operationId = getNextOperationId();
      console.log(
        `[DEBUG-PICKER] Processing deliveryDate in DeliveryDatePicker - operation #${operationId}:`,
        {
          groupId,
          deliveryDate,
          timestamp: new Date().toISOString(),
        }
      );

      try {
        // Using DateService to parse the date regardless of format
        if (deliveryDate.includes("-")) {
          // API format: YYYY-MM-DD
          const date = DateService.createDateFromApiFormat(deliveryDate);
          setSelectedDate(date);
        } else if (deliveryDate.includes(" / ")) {
          // UI format: DD / MM / YYYY
          const apiFormat = DateService.convertToApiFormat(deliveryDate);
          if (apiFormat) {
            const date = DateService.createDateFromApiFormat(apiFormat);
            setSelectedDate(date);
          } else {
            console.warn("Could not convert date to API format:", deliveryDate);
            // Fallback to minDate with noon time
            setSelectedDate(minDate);
          }
        } else {
          console.warn("Unrecognized date format:", deliveryDate);
          // Fallback to minDate with noon time
          setSelectedDate(minDate);
        }
      } catch (error) {
        console.error("Error parsing delivery date:", error, deliveryDate);
        // Fallback to minDate with noon time
        setSelectedDate(minDate);
      }
    } else {
      // No delivery date provided - use minDate but don't open calendar
      console.log(
        `[DEBUG-PICKER] No deliveryDate provided for group ${groupId}, using minDate`
      );
      setShowPlaceholder(true);
      setSelectedDate(null);
    }
  }, [deliveryDate, minDate, groupId]);

  const handleDateChange = (date: Date | null) => {
    const operationId = getNextOperationId();

    if (date) {
      // Always respect the effective minDate constraint (different for rápido vs standard)
      const effectiveDate = date < effectiveMinDate ? effectiveMinDate : date;

      // Use DateService to create a noon time date to prevent timezone issues
      const noonDate = DateService.createLimaDate(
        effectiveDate.getFullYear(),
        effectiveDate.getMonth(),
        effectiveDate.getDate()
      );

      // Format date using the UI format (DD / MM / YYYY)
      const formattedDate = `${noonDate
        .getDate()
        .toString()
        .padStart(2, "0")} / ${(noonDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")} / ${noonDate.getFullYear()}`;

      console.log(
        `[DEBUG-PICKER] Date selected in DeliveryDatePicker - operation #${operationId}:`,
        {
          groupId,
          formattedDate,
          rawDate: noonDate.toISOString(),
          timestamp: new Date().toISOString(),
        }
      );

      // Update selected date locally for optimistic update FIRST
      setSelectedDate(noonDate);
      setShowPlaceholder(false);

      // Then immediately trigger callback for optimistic update in parent
      onDateChange(formattedDate);

      // Close calendar AFTER updates to prevent race conditions
      setIsCalendarOpen(false);
    }
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // Check if click is within the calendar (portal)
        const calendarElement = document.querySelector(".react-datepicker");
        if (
          !calendarElement ||
          !calendarElement.contains(event.target as Node)
        ) {
          setIsCalendarOpen(false);
        }
      }
    };

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCalendarOpen]);

  // Calculate maximum selectable date (30 days from minimum date)
  const maxDate = DateService.addDaysLima(minDate, 30);

  // Calculate the effective minDate for the date picker
  const effectiveMinDate = (() => {
    // For rápido delivery method, calculate next 2 calendar days if availableDates not provided
    if (deliveryMethod === "rapido") {
      if (availableDates && availableDates.length > 0) {
        // Use provided available dates
        const earliestDate = availableDates[0];
        console.log(
          `🔧 [DELIVERY-DATE-PICKER] Using availableDates for rápido:`,
          {
            groupId,
            deliveryMethod,
            originalMinDate: {
              iso: minDate.toISOString(),
              local: minDate.toLocaleDateString("es-PE"),
            },
            effectiveMinDate: {
              iso: earliestDate.toISOString(),
              local: earliestDate.toLocaleDateString("es-PE"),
            },
            availableDatesCount: availableDates.length,
          }
        );
        return earliestDate;
      } else {
        // Fallback: Calculate next 2 calendar days from today
        const today = new Date();
        const tomorrow = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() + 1,
          12,
          0,
          0
        );
        console.log(
          `🔧 [DELIVERY-DATE-PICKER] Using fallback rápido calculation (tomorrow):`,
          {
            groupId,
            deliveryMethod,
            originalMinDate: {
              iso: minDate.toISOString(),
              local: minDate.toLocaleDateString("es-PE"),
            },
            fallbackMinDate: {
              iso: tomorrow.toISOString(),
              local: tomorrow.toLocaleDateString("es-PE"),
            },
            reason: "No availableDates provided for rápido method",
          }
        );
        return tomorrow;
      }
    }

    // Standard mode: Use the calculated minDate from CartGroup
    console.log(`🔧 [DELIVERY-DATE-PICKER] Using standard minDate:`, {
      groupId,
      deliveryMethod: deliveryMethod || "standard",
      minDate: {
        iso: minDate.toISOString(),
        local: minDate.toLocaleDateString("es-PE"),
      },
    });
    return minDate;
  })();

  // Determine which dates to include based on delivery method and availableDates
  const includeDates = (() => {
    if (deliveryMethod === "rapido") {
      if (availableDates && availableDates.length > 0) {
        // Use provided available dates for rápido
        const sortedDates = [...availableDates].sort(
          (a, b) => a.getTime() - b.getTime()
        );
        const filteredDates = sortedDates.filter((date) => date <= maxDate); // Only filter against maxDate

        console.log(
          `🔧 [DELIVERY-DATE-PICKER] Processing includeDates for rápido with availableDates:`,
          {
            groupId,
            deliveryMethod,
            originalDates: availableDates.map((d) => ({
              iso: d.toISOString(),
              local: d.toLocaleDateString("es-PE"),
            })),
            sortedDates: sortedDates.map((d) => ({
              iso: d.toISOString(),
              local: d.toLocaleDateString("es-PE"),
            })),
            filteredDates: filteredDates.map((d) => ({
              iso: d.toISOString(),
              local: d.toLocaleDateString("es-PE"),
            })),
          }
        );

        return filteredDates;
      } else {
        // Fallback: Generate next 2 calendar days for rápido
        const today = new Date();
        const tomorrow = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() + 1,
          12,
          0,
          0
        );
        const dayAfterTomorrow = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() + 2,
          12,
          0,
          0
        );
        const rapidoDates = [tomorrow, dayAfterTomorrow];

        console.log(
          `🔧 [DELIVERY-DATE-PICKER] Using fallback rápido dates (next 2 calendar days):`,
          {
            groupId,
            deliveryMethod,
            rapidoDates: rapidoDates.map((d) => ({
              iso: d.toISOString(),
              local: d.toLocaleDateString("es-PE"),
            })),
            reason: "No availableDates provided for rápido method",
          }
        );

        return rapidoDates;
      }
    }

    // Standard method: no date restriction (uses minDate to maxDate range)
    return undefined;
  })();

  // Log available dates for debugging with business logic context
  if (deliveryMethod === "express") {
    console.log(
      `📅 [DELIVERY-DATE-PICKER] EXPRESS mode - automatic today's date for group ${groupId}:`,
      {
        deliveryMethod,
        todayDate: new Date().toLocaleDateString("es-PE"),
        automaticDate: {
          iso: new Date().toISOString(),
          local: new Date().toLocaleDateString("es-PE"),
        },
        selectedDate: selectedDate
          ? {
              iso: selectedDate.toISOString(),
              local: selectedDate.toLocaleDateString("es-PE"),
            }
          : "not yet set",
        businessRule:
          "Express delivery uses today's date automatically - no user selection needed",
        uiMode: "display-only (no date picker)",
      }
    );
  } else if (deliveryMethod === "rapido") {
    console.log(
      `📅 [DELIVERY-DATE-PICKER] RÁPIDO mode - using next 2 calendar days for group ${groupId}:`,
      {
        deliveryMethod,
        todayDate: new Date().toLocaleDateString("es-PE"),
        totalIncludeDates: includeDates?.length || 0,
        minDateFromCartGroup: {
          iso: minDate.toISOString(),
          local: minDate.toLocaleDateString("es-PE"),
        },
        effectiveMinDate: {
          iso: effectiveMinDate.toISOString(),
          local: effectiveMinDate.toLocaleDateString("es-PE"),
        },
        includeDates:
          includeDates?.map((d) => ({
            iso: d.toISOString(),
            local: d.toLocaleDateString("es-PE"),
          })) || [],
        businessRule: "Next 2 calendar days from today",
        sourceOfDates: availableDates
          ? "From DeliveryMethodSelector"
          : "Generated fallback",
      }
    );
  } else {
    console.log(
      `📅 [DELIVERY-DATE-PICKER] STANDARD mode - using minDate range for group ${groupId}:`,
      {
        deliveryMethod: deliveryMethod || "standard",
        minDateFromCartGroup: {
          iso: minDate.toISOString(),
          local: minDate.toLocaleDateString("es-PE"),
          daysFromToday: Math.ceil(
            (minDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
          ),
        },
        maxDate: {
          iso: maxDate.toISOString(),
          local: maxDate.toLocaleDateString("es-PE"),
        },
        businessRule: "Latest delivery date among all products in group",
        mode: "normal-date-range",
      }
    );
  }

  // Format the date for display using DateService
  const getFormattedDisplayDate = () => {
    if (showPlaceholder) return "";
    if (!selectedDate) return "";

    // Using consistent DD / MM / YYYY format
    return `${selectedDate.getDate().toString().padStart(2, "0")} / ${(
      selectedDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")} / ${selectedDate.getFullYear()}`;
  };

  // Calculate optimal position for the calendar
  const calculateCalendarPosition = (): CSSProperties => {
    if (!containerRef.current || !inputRef.current) return {};

    const inputRect = inputRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;

    // Calendar dimensions estimation
    const calendarHeight = 350; // Approximate height of calendar
    const bottomSpacing = 20; // Space to keep at the bottom of viewport

    if (isMobile) {
      // On mobile, position as bottom sheet
      return {
        position: "fixed",
        bottom: "0",
        left: "0",
        right: "0",
        width: "100%",
        maxWidth: "100%",
        borderRadius: "12px 12px 0 0",
        borderBottom: "none",
        boxShadow: "0 -4px 10px rgba(0, 0, 0, 0.1)",
      };
    }

    // For desktop, check if we have enough space below the input
    const spaceBelow = viewportHeight - inputRect.bottom - bottomSpacing;
    if (spaceBelow >= calendarHeight) {
      // Position below input
      return {
        position: "absolute",
        top: `${inputRect.height + 8}px`, // Below input with spacing
        left: "0",
        minWidth: "320px",
        zIndex: 1000,
      };
    } else {
      // Not enough space below - position above input if possible
      return {
        position: "absolute",
        bottom: `${inputRect.height + 8}px`, // Above input with spacing
        left: "0",
        minWidth: "320px",
        zIndex: 1000,
      };
    }
  };

  // Prepare calendar position when opening
  const handleOpenCalendar = () => {
    console.log(`[DEBUG-CALENDAR] Opening calendar for group ${groupId}`);
    const position = calculateCalendarPosition();
    calendarPositionRef.current = position;
    setIsCalendarOpen(true);
  };

  // Custom render of calendar in portal
  const renderCalendarPortal = () => {
    if (!isCalendarOpen || !portalContainer) return null;

    // Debug: Log DatePicker props when calendar opens
    console.log(
      `🗓️ [DELIVERY-DATE-PICKER] Rendering DatePicker for group ${groupId}:`,
      {
        groupId,
        effectiveMinDate: {
          iso: effectiveMinDate.toISOString(),
          local: effectiveMinDate.toLocaleDateString("es-PE"),
        },
        maxDate: {
          iso: maxDate.toISOString(),
          local: maxDate.toLocaleDateString("es-PE"),
        },
        includeDates: includeDates
          ? {
              count: includeDates.length,
              dates: includeDates.map((d) => ({
                iso: d.toISOString(),
                local: d.toLocaleDateString("es-PE"),
              })),
            }
          : "undefined",
        selectedDate: selectedDate
          ? {
              iso: selectedDate.toISOString(),
              local: selectedDate.toLocaleDateString("es-PE"),
            }
          : "null",
      }
    );

    const calendarContent = (
      <div className={styles.calendarPortal}>
        <div
          className={styles.calendarOverlay}
          onClick={() => setIsCalendarOpen(false)}
        />
        <div
          className={`${styles.calendarContainer} ${
            window.innerWidth < 768 ? styles.mobileCalendar : ""
          }`}
          style={calendarPositionRef.current}
        >
          <DatePicker
            selected={selectedDate || effectiveMinDate}
            onChange={handleDateChange}
            minDate={effectiveMinDate}
            maxDate={maxDate}
            includeDates={includeDates} // Restrict to available dates if provided
            inline
            calendarClassName={styles.calendar}
            locale="es"
            dayClassName={(date) => {
              const day = date.getDay();
              return day === 0 || day === 6 ? styles.weekend : styles.weekday;
            }}
            open={true}
            onClickOutside={() => setIsCalendarOpen(false)}
          />
          <div className={styles.calendarActions}>
            <button
              className={styles.calendarCancelButton}
              onClick={() => setIsCalendarOpen(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(calendarContent, portalContainer);
  };

  // For Express delivery, show a simple display without interactive date picker
  if (deliveryMethod === "express") {
    return (
      <div className={styles.datePickerWrapper} ref={containerRef}>
        <div className={`${styles.datePicker} ${styles.expressDelivery}`}>
          <span className={styles.expressLabel}>
            📦 Entrega Express - Hoy:{" "}
            {selectedDate
              ? getFormattedDisplayDate()
              : new Date().toLocaleDateString("es-PE")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.datePickerWrapper} ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={getFormattedDisplayDate()}
        onClick={() => handleOpenCalendar()}
        readOnly
        className={`${styles.datePicker} ${
          showPlaceholder ? styles.showPlaceholder : ""
        }`}
        placeholder="Ingresar fecha"
      />
      <span
        className={styles.calendarIcon}
        onClick={(e) => {
          e.stopPropagation();
          handleOpenCalendar();
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </span>
      {renderCalendarPortal()}
    </div>
  );
};

export default DeliveryDatePicker;
